import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, Loader, RefreshCw, X, Instagram, Youtube, Music, Target, Lightbulb, Zap, Check, AtSign, FileText } from 'lucide-react';
import { generateNicheIdeas, generateContentPlan, generateChannelName } from '../../../services/openai';
import { useSocialMediaStore } from '../../../stores/socialMediaStore';
import { useContentPlanStore } from '../../../stores/contentPlanStore';
import { useUserStore } from '../../../stores/userStore';
import { TRANSITIONS } from '../../../utils/animations';
import toast from 'react-hot-toast';

interface NicheIdea {
  niche: string;
  description: string;
  emoji: string;
  contentIdeas: string[];
}

interface NicheGeneratorProps {
  onClose?: () => void;
}

export default function NicheGenerator({ onClose }: NicheGeneratorProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<'platform' | 'topic' | 'ideas' | 'channel-name' | 'prompts' | 'generating-plan'>('platform');
  const [isGenerating, setIsGenerating] = useState(false);
  const [topic, setTopic] = useState('');
  const [nicheIdeas, setNicheIdeas] = useState<NicheIdea[]>([]);
  const [selectedIdea, setSelectedIdea] = useState<NicheIdea | null>(null);
  const [channelName, setChannelName] = useState('');
  const [isGeneratingName, setIsGeneratingName] = useState(false);
  const [prompts, setPrompts] = useState({
    objective: '',
    contentType: '',
    bestPerforming: '',
    targetAudience: ''
  });

  const { accounts, connectAccount } = useSocialMediaStore();
  const { addPlan, canUseNicheGenerator, incrementNicheUsage, currentMonthNiche, getNicheLimit } = useContentPlanStore();
  const hasUnlimitedAccess = useUserStore((state) => state.hasUnlimitedAccess());
  const nicheLimit = getNicheLimit();
  const connectedAccounts = accounts.filter(account => account.connected);

  const handlePlatformSelect = (platform: 'instagram' | 'youtube' | 'tiktok') => {
    if (!canUseNicheGenerator()) {
      toast.error(`Você atingiu o limite de ${nicheLimit} gerações de nicho para este mês`);
      return;
    }
    
    connectAccount(platform);
    setStep('topic');
  };

  const handleTopicSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!topic.trim()) {
      toast.error('Por favor, digite sobre o que você quer postar');
      return;
    }

    if (!canUseNicheGenerator()) {
      toast.error(`Você atingiu o limite de ${nicheLimit} gerações de nicho para este mês`);
      return;
    }

    // Increment usage before generating ideas
    if (!incrementNicheUsage()) {
      toast.error(`Você atingiu o limite de ${nicheLimit} gerações de nicho para este mês`);
      return;
    }

    setIsGenerating(true);
    try {
      const ideas = await generateNicheIdeas(topic);
      setNicheIdeas(ideas);
      setStep('ideas');
    } catch (error) {
      toast.error('Erro ao gerar ideias. Por favor, tente novamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateIdeas = async () => {
    setIsGenerating(true);
    try {
      const ideas = await generateNicheIdeas(topic);
      setNicheIdeas(ideas);
    } catch (error) {
      toast.error('Erro ao gerar ideias. Por favor, tente novamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectIdea = async (idea: NicheIdea) => {
    setSelectedIdea(idea);
    setIsGeneratingName(true);
    
    try {
      // Generate a creative channel name based on the niche
      const generatedName = await generateChannelName(idea.niche, idea.description);
      setChannelName(`@${generatedName}`);
    } catch (error) {
      // Fallback to simple name if generation fails
      setChannelName(`@${idea.niche.toLowerCase().replace(/\s+/g, '')}official`);
    } finally {
      setIsGeneratingName(false);
      setStep('channel-name');
    }
  };

  const handleChannelNameSubmit = () => {
    if (selectedIdea) {
      setPrompts({
        objective: `Criar conteúdo envolvente sobre ${selectedIdea.niche}`,
        contentType: selectedIdea.contentIdeas[0],
        bestPerforming: selectedIdea.contentIdeas[1],
        targetAudience: 'Pessoas interessadas em ' + selectedIdea.description
      });
      setStep('prompts');
    }
  };

  const handleGeneratePlan = async () => {
    setStep('generating-plan');
    try {
      const content = await generateContentPlan(prompts);
      const plan = {
        title: channelName,
        content
      };
      addPlan(plan);
      
      toast.success('Plano gerado com sucesso! Redirecionando para Meus Planos...', {
        duration: 3000
      });
      
      if (onClose) {
        onClose();
      }
      
      setTimeout(() => {
        navigate('/dashboard/planos');
      }, 1000);
    } catch (error) {
      toast.error('Erro ao gerar plano. Por favor, tente novamente.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-lg w-full mx-4 p-6 shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {step === 'platform' && 'Conecte sua Plataforma'}
              {step === 'topic' && 'Sobre o que você quer postar?'}
              {step === 'ideas' && 'Escolha seu Nicho'}
              {step === 'channel-name' && 'Nome do Canal'}
              {step === 'prompts' && 'Detalhes do Canal'}
              {step === 'generating-plan' && 'Gerando Plano'}
            </h2>
            {!hasUnlimitedAccess && step === 'platform' && (
              <div className="flex items-center mt-1">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-indigo-600 rounded-full"
                    style={{ width: `${Math.min((currentMonthNiche / nicheLimit) * 100, 100)}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 ml-2">
                  {currentMonthNiche} de {nicheLimit}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            {['platform', 'topic', 'ideas', 'channel-name', 'prompts'].map((s, index) => (
              <div 
                key={s} 
                className={`flex items-center ${index < ['platform', 'topic', 'ideas', 'channel-name', 'prompts'].indexOf(step) + 1 ? 'text-indigo-600' : 'text-gray-400'}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  index < ['platform', 'topic', 'ideas', 'channel-name', 'prompts'].indexOf(step) + 1 
                    ? 'bg-indigo-100 text-indigo-600' 
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  {index + 1}
                </div>
                {index < 4 && (
                  <div className={`w-full h-1 ${
                    index < ['platform', 'topic', 'ideas', 'channel-name'].indexOf(step) + 1 
                      ? 'bg-indigo-600' 
                      : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content - with scrollable area for ideas */}
        <div className="flex-1 overflow-y-auto">
          {step === 'platform' && (
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => handlePlatformSelect('instagram')}
                className="p-6 bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg border border-pink-200 hover:shadow-md transition-all flex flex-col items-center transform hover:scale-105"
              >
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-4 shadow-sm">
                  <Instagram className="h-8 w-8 text-pink-600" />
                </div>
                <span className="font-medium text-gray-900">Instagram</span>
              </button>
              <button
                onClick={() => handlePlatformSelect('youtube')}
                className="p-6 bg-gradient-to-br from-red-50 to-orange-50 rounded-lg border border-red-200 hover:shadow-md transition-all flex flex-col items-center transform hover:scale-105"
              >
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-4 shadow-sm">
                  <Youtube className="h-8 w-8 text-red-600" />
                </div>
                <span className="font-medium text-gray-900">YouTube</span>
              </button>
              <button
                onClick={() => handlePlatformSelect('tiktok')}
                className="p-6 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg border border-cyan-200 hover:shadow-md transition-all flex flex-col items-center transform hover:scale-105"
              >
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-4 shadow-sm">
                  <Music className="h-8 w-8 text-black" />
                </div>
                <span className="font-medium text-gray-900">TikTok</span>
              </button>
            </div>
          )}

          {step === 'topic' && (
            <div className="space-y-6">
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                <div className="flex items-start space-x-3">
                  <Lightbulb className="h-5 w-5 text-indigo-600 mt-0.5" />
                  <div>
                    <p className="text-indigo-900 font-medium">Dica para melhores resultados</p>
                    <p className="text-indigo-700 text-sm mt-1">
                      Seja específico sobre o tema que você quer explorar. Por exemplo, em vez de "jogos", tente "Minecraft" ou "jogos de estratégia".
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <Target className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Ex: carros, culinária, tecnologia..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  disabled={isGenerating}
                />
              </div>
              
              <button
                onClick={handleTopicSubmit}
                disabled={isGenerating || !topic.trim()}
                className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transform hover:scale-105"
              >
                {isGenerating ? (
                  <>
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Gerando...
                  </>
                ) : (
                  <>
                    Continuar
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </button>
            </div>
          )}

          {step === 'ideas' && (
            <div className="space-y-4">
              {isGenerating ? (
                <div className="text-center py-12">
                  <Loader className="animate-spin h-12 w-12 text-indigo-600 mx-auto mb-4" />
                  <p className="text-gray-600">Gerando ideias de nicho...</p>
                  <p className="text-sm text-gray-500 mt-2">Isso pode levar alguns segundos</p>
                </div>
              ) : (
                <>
                  <div className="flex justify-end mb-2">
                    <button
                      onClick={handleRegenerateIdeas}
                      className="text-indigo-600 hover:text-indigo-700 flex items-center text-sm"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Gerar Novas
                    </button>
                  </div>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {nicheIdeas.map((idea, index) => (
                      <div
                        key={index}
                        onClick={() => handleSelectIdea(idea)}
                        className="p-5 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg border border-indigo-200 hover:shadow-md transition-all cursor-pointer transform hover:scale-[1.02]"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                            <span className="text-2xl">{idea.emoji}</span>
                          </div>
                          <h4 className="font-medium text-gray-900 text-lg">{idea.niche}</h4>
                        </div>
                        <p className="text-gray-600 mb-3">{idea.description}</p>
                        <div className="bg-white/80 p-3 rounded-lg">
                          <p className="text-sm font-medium text-gray-900 mb-2">Ideias de conteúdo:</p>
                          <ul className="space-y-1">
                            {idea.contentIdeas.map((contentIdea, i) => (
                              <li key={i} className="text-sm text-gray-600 flex items-start">
                                <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0">
                                  <Check className="h-3 w-3 text-indigo-600" />
                                </div>
                                {contentIdea}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'channel-name' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-5 rounded-lg border border-indigo-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <span className="text-2xl">{selectedIdea?.emoji}</span>
                  </div>
                  <h4 className="font-medium text-gray-900 text-lg">{selectedIdea?.niche}</h4>
                </div>
                <p className="text-gray-600">{selectedIdea?.description}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do Canal
                </label>
                <div className="relative">
                  <AtSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    value={channelName}
                    onChange={(e) => setChannelName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    placeholder="@seunome"
                    disabled={isGeneratingName}
                  />
                  {isGeneratingName && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Loader className="h-5 w-5 text-indigo-600 animate-spin" />
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Nome sugerido pela IA com base no seu nicho. Você pode editá-lo se preferir.
                </p>
              </div>
              
              <button
                onClick={handleChannelNameSubmit}
                disabled={!channelName.trim() || isGeneratingName}
                className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
              >
                Continuar
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          )}

          {step === 'prompts' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-4 rounded-lg border border-indigo-200 mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <span className="text-xl">{selectedIdea?.emoji}</span>
                  </div>
                  <h4 className="font-medium text-gray-900">{channelName}</h4>
                </div>
                <p className="text-sm text-gray-600">{selectedIdea?.description}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Objetivo Principal
                </label>
                <input
                  type="text"
                  value={prompts.objective}
                  onChange={(e) => setPrompts({ ...prompts, objective: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Conteúdo
                </label>
                <input
                  type="text"
                  value={prompts.contentType}
                  onChange={(e) => setPrompts({ ...prompts, contentType: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conteúdo de Melhor Desempenho
                </label>
                <input
                  type="text"
                  value={prompts.bestPerforming}
                  onChange={(e) => setPrompts({ ...prompts, bestPerforming: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Público-Alvo
                </label>
                <input
                  type="text"
                  value={prompts.targetAudience}
                  onChange={(e) => setPrompts({ ...prompts, targetAudience: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleGeneratePlan}
                disabled={!prompts.objective.trim() || !prompts.contentType.trim() || !prompts.targetAudience.trim()}
                className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transform hover:scale-105"
              >
                Gerar Plano de Conteúdo
                <Zap className="ml-2 h-5 w-5" />
              </button>
            </div>
          )}

          {step === 'generating-plan' && (
            <div className="text-center py-12">
              <div className="relative w-24 h-24 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full border-4 border-indigo-200 border-opacity-50"></div>
                <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <FileText className="h-10 w-10 text-indigo-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Gerando seu plano personalizado...</h3>
              <p className="text-gray-600">Estamos criando um plano detalhado baseado no nicho selecionado</p>
              <div className="mt-6 max-w-md mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-600 rounded-full animate-pulse w-full"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}